import { User } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getData, postData } from "../../api/api";
import ComDateConverter from "./../../Components/ComDateConverter/ComDateConverter";
import { Pagination, Spin, Input, Modal, Button } from "antd"; 
import { useNotification } from "../../Notification/Notification";
import ComUpImg from "../../Components/ComUpImg/ComUpImg";
import { useStorage } from "../../hooks/useLocalStorage";
import { SearchOutlined } from "@ant-design/icons";

export default function Forums() {
  // State quản lý danh sách bài đăng
  const [posts, setPosts] = useState([]); 

  // State quản lý trạng thái tải dữ liệu
  const [loading, setLoading] = useState(false);

  // State lưu tổng số bài đăng để phân trang
  const [totalPosts, setTotalPosts] = useState(0);

  // State quản lý trang hiện tại
  const [page, setPage] = useState(1);

  // Hook để hiển thị thông báo
  const { notificationApi } = useNotification();

  // State lưu trữ các hình ảnh được chọn
  const [images, setImages] = useState([]); 

  // State quản lý nội dung bài đăng
  const [content, setContent] = useState(""); 

  // State quản lý trạng thái nút submit
  const [disabled, setDisabled] = useState(false);

  // Hook lưu trữ thông tin người dùng trong localStorage
  const [use, setUse] = useStorage("use", null);

  // State để reset danh sách file
  const [resetFileList, setResetFileList] = useState(false);

  // State quản lý hiển thị modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchContent, setSearchContent] = useState("");


  // Hàm lấy dữ liệu bài đăng từ API
  const callData = () => {
    // Xây dựng URL với phân trang và tìm kiếm
    let url = `/posts?status=Active&pageNumber=${page - 1}&pageSize=10`;

    // Thêm điều kiện tìm kiếm nếu có nội dung
   if (searchContent.trim()) {
    url += `&content=${encodeURIComponent(searchContent)}`;
  }

    // Bật trạng thái loading
    setLoading(true);

    // Gọi API để lấy dữ liệu
    getData(url)
    .then((e) => {
      setPosts(e.data.data);
      setTotalPosts(e.data.pagination.totalRow);
      setLoading(false);
    })
    .catch((error) => {
      console.log(error);
      setLoading(false);
    });
};

  // Effect để tải dữ liệu khi component mount hoặc thay đổi trang
  useEffect(() => {
    setLoading(true);
    callData();
  }, [page]);

  // Xử lý thay đổi trang
  const handlePageChange = (page) => {
    setPage(page);
  };

  // Hàm submit bài đăng mới
  const onSubmit = () => {
    // Kiểm tra hình ảnh
    if (!images || images.length === 0) {
      notificationApi("error", "Invalid Image", "Please select an image.");
      return;
    }

    // Kiểm tra nội dung
    if (!content.trim()) {
      notificationApi(
        "error",
        "Invalid Content",
        "Please enter content for the post."
      );
      return;
    }

    // Vô hiệu hóa nút submit
    setDisabled(true);

    // Tạo FormData để gửi file và dữ liệu
    const formData = new FormData();

    // Thêm nhiều hình ảnh vào FormData
    images.forEach((file) => formData.append("images", file));

    // Thêm nội dung bài đăng
    formData.append("content", content);

    // Gọi API đăng bài
    postData("/posts", formData)
      .then((response) => {
        // Hiển thị thông báo thành công
        notificationApi("success", "Success", "Post created successfully");
        
        // Reset trạng thái
        setDisabled(false);
        callData(); // Tải lại danh sách bài đăng
        setContent(""); // Xóa nội dung
        setImages([]); // Xóa hình ảnh
        setResetFileList(!resetFileList); // Reset danh sách file
        setIsModalVisible(false); // Đóng modal
      })
      .catch((error) => {
        // Xử lý lỗi
        console.error(error);
        setDisabled(false);
        notificationApi("error", "Failed", "Please try again");
      });
  };

  // Xử lý thay đổi hình ảnh
  const onChange = (data) => {
    // Chuyển đổi file hình ảnh
    const selectedImages = data;
    const newImages = selectedImages.map((file) => file.originFileObj);
    setImages(newImages);
  };

  // Hiển thị modal tạo bài đăng
  const showModal = () => {
    setIsModalVisible(true);
  };

  // Đóng modal
  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // Xử lý tìm kiếm
  const handleSearch = () => {
    callData();
  };

  // Xử lý sự kiện nhấn phím Enter để tìm kiếm
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      callData();
    }  
  };

  return (
    <div className="max-w-4xl mx-auto  mt-8 min-h-[50vh]">
      {/* Ô tìm kiếm */}
      <div className="mb-4 flex items-center">
        <Input
          placeholder="Search posts..."
          value={searchContent}
          onChange={(e) => setSearchContent(e.target.value)}
          onKeyPress={handleKeyPress}
          suffix={
            <SearchOutlined
              onClick={handleSearch}
              style={{ cursor: "pointer" }}
            />
          }
        />
      </div>

      {/* Nút tạo bài đăng (chỉ hiển thị khi đã đăng nhập) */}
      {use?.id && (
        <div className="mb-4 text-center flex justify-end">
          <Button
            type="primary"
            onClick={showModal}
            className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
          >
            Create New Post
          </Button>
        </div>
      )}

      {/* Phần hiển thị danh sách bài đăng */}
      <div className="divide-y">
        {loading ? (
          // Hiển thị spinner khi đang tải
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : (
          // Render danh sách bài đăng
          posts.map((post) => (
            <div key={post.id} className="p-4 hover:bg-gray-50">
              {/* Chi tiết từng bài đăng */}
              <div className="flex gap-4">
                <div>
                  <img
                    src={post?.createdByNavigation?.avatar}
                    alt={post.id}
                    className="w-8 h-8 rounded-full"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Link
                      to={`/Forums/${post.id}`}
                      className="text-lg font-medium text-blue-600 hover:text-blue-800  line-clamp-3 "
                    >
                      {post.content}
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <a href="#" className="hover:underline">
                      {post.createdByNavigation.firstName}{" "}
                      {post.createdByNavigation.lastName}
                    </a>
                    <span>·</span>
                    <span>
                      <ComDateConverter>{post.createAt}</ComDateConverter>
                    </span>
                  </div>
                </div>
                {/* Thống kê bài đăng */}
                <div className="flex items-center gap-8 text-sm text-gray-600">
                  <div className="text-center">
                    <div className="font-semibold">
                      {post?.comments?.length}
                    </div>
                    <div>Replies</div>
                  </div>
                  <div className="text-center">
                    <div className="font-semibold">
                      {post?.reactions.length}
                    </div>
                    <div>Likes</div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Phân trang */}
      <div className="flex justify-center mt-4">
        <Pagination
          current={page}
          pageSize={10}
          total={totalPosts}
          onChange={handlePageChange}
          showSizeChanger={false}
        />
      </div>

      {/* Modal tạo bài đăng mới */}
      <Modal
        title="Create New Post"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        className="rounded-lg"
        width={600}
      >
        {/* Textarea nhập nội dung */}
        <Input.TextArea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={4}
          placeholder="Enter post content"
        />
        <h2 className="text-xl mb-4">Image</h2>

        {/* Component upload ảnh */}
        <ComUpImg
          onChange={onChange}
          error={images.length === 0 ? "Please select an image" : ""}
          required
          resetFileList={resetFileList}
          multiple
        />
        
        {/* Nút submit */}
        <div className="mt-4 flex justify-center">
          <Button
            type="primary"
            onClick={onSubmit}
            disabled={disabled}
            loading={disabled}
          >
            {disabled ? "Creating..." : "Create Post"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}